import React, { useState, useEffect } from 'react';
import { checkAuth, authenticatedRequest } from '../utils/auth';
import './OffersList.css';

const OffersList = () => {
  const [offers, setOffers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [selectedOffer, setSelectedOffer] = useState(null);

  useEffect(() => {
    const initializeComponent = async () => {
      const isAuthenticated = await checkAuth();
      if (!isAuthenticated) {
        return;
      }
      
      await fetchOffers();
    };
    
    initializeComponent();
  }, []);

  const fetchOffers = async () => {
    try {
      setLoading(true);
      const response = await authenticatedRequest('GET', '/offers');
      
      if (response.data.ok) {
        setOffers(response.data.offers);
      } else {
        setError(response.data.message || 'خطا در دریافت لیست آفرها');
      }
    } catch (err) {
      console.error('Error fetching offers:', err);
      setError('خطا در اتصال به سرور');
    } finally {
      setLoading(false);
    }
  };

  const handleEditOffer = (offer) => {
    setSelectedOffer(offer);
  };

  const handleDeleteOffer = async (offerId) => {
    if (!window.confirm('آیا از حذف این آفر اطمینان دارید؟')) {
      return;
    }

    try {
      const response = await authenticatedRequest('DELETE', `/offers/${offerId}`);
      
      if (response.data.ok) {
        setOffers(offers.filter(offer => offer._id !== offerId));
        setError(''); // Clear any previous errors
      } else {
        setError(response.data.message || 'خطا در حذف آفر');
      }
    } catch (err) {
      console.error('Error deleting offer:', err);
      setError('خطا در حذف آفر');
    }
  };

  const formatDate = (dateString) => {
    if (!dateString) return 'تاریخ نامشخص';
    const date = new Date(dateString);
    return date.toLocaleDateString('fa-IR');
  };

  const formatTime = (timeArray) => {
    if (!timeArray || timeArray.length === 0) return 'زمان نامشخص';
    return timeArray.map(time => `${time.label}: ${time.startTime} - ${time.endTime}`).join('، ');
  };

  if (loading) {
    return (
      <div className="offers-list-container">
        <div className="loading">در حال بارگذاری...</div>
      </div>
    );
  }

  const handleBack = () => {
    window.history.back();
  };

  return (
    <div className="offers-list-container">
      <div className="offers-list-card">
        <div className="offers-list-header">
          <div className="header-actions">
            <button onClick={handleBack} className="back-button">
              <span className="back-arrow">←</span> بازگشت
            </button>
          </div>
          <h2>لیست آفرها</h2>
          <p>مدیریت و ویرایش آفرهای موجود</p>
        </div>

        {error && <div className="error-message">{error}</div>}

        {offers.length === 0 ? (
          <div className="no-offers">
            <p>هیچ آفری یافت نشد</p>
          </div>
        ) : (
          <div className="offers-grid">
            {offers.map(offer => (
              <div key={offer._id} className="offer-card">
                <div className="offer-header">
                  <h3>{offer.title}</h3>
                  <span className={`offer-type ${offer.type}`}>{offer.type}</span>
                </div>

                <div className="offer-content">
                  <p className="offer-description">{offer.messageDescription}</p>
                  
                  <div className="offer-details">
                    <div className="detail-item">
                      <strong>نوع تبلیغ:</strong> {offer.adType || 'نامشخص'}
                    </div>
                    
                    <div className="detail-item">
                      <strong>رستوران:</strong> {offer.restaurantName || (offer.restaurant?.name) || 'نامشخص'}
                    </div>
                    
                    <div className="detail-item">
                      <strong>تاریخ انقضا:</strong> {formatDate(offer.expirationDate)}
                    </div>
                    
                    <div className="detail-item">
                      <strong>محدوده فاصله:</strong> {offer.distanceLimit} متر
                    </div>
                    
                    {offer.time && offer.time.length > 0 && (
                      <div className="detail-item">
                        <strong>زمان‌ها:</strong> {formatTime(offer.time)}
                      </div>
                    )}
                  </div>

                  {offer.picture && offer.picture !== 'uploadspublic/offers/no-offer-image.png' && (
                    <div className="offer-image">
                      <img src={offer.picture.startsWith('http') ? offer.picture : `https://apipublic.menuoff.com/${offer.picture}`} alt={offer.title} />
                    </div>
                  )}
                </div>

                <div className="offer-actions">
                  <button
                    onClick={() => handleEditOffer(offer)}
                    className="edit-btn"
                  >
                    ویرایش
                  </button>
                  <button
                    onClick={() => handleDeleteOffer(offer._id)}
                    className="delete-btn"
                  >
                    حذف
                  </button>
                </div>

                {offer.isEdited?.status && (
                  <div className="edited-badge">ویرایش شده</div>
                )}
              </div>
            ))}
          </div>
        )}
      </div>

      {selectedOffer && (
        <EditOfferModal
          offer={selectedOffer}
          onClose={() => setSelectedOffer(null)}
          onUpdate={fetchOffers}
        />
      )}
    </div>
  );
};

// Helper function to format datetime-local for backend
const formatDateTimeForBackend = (datetimeLocal) => {
  if (!datetimeLocal) return '';
  
  // datetime-local gives us a string like "2024-01-15T23:30" without timezone info
  // We need to append the local timezone offset to preserve the user's input time
  
  // Create a date object from the input
  const date = new Date(datetimeLocal);
  
  // Get the timezone offset in minutes (for Iran it's -210, meaning UTC+3:30)
  const offsetMinutes = -date.getTimezoneOffset();
  
  // Convert offset to hours and minutes
  const offsetHours = Math.floor(Math.abs(offsetMinutes) / 60);
  const offsetMins = Math.abs(offsetMinutes) % 60;
  const offsetSign = offsetMinutes >= 0 ? '+' : '-';
  
  // Format: 2025-09-30T20:20:00.000+03:30
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, '0');
  const day = String(date.getDate()).padStart(2, '0');
  const hours = String(date.getHours()).padStart(2, '0');
  const minutes = String(date.getMinutes()).padStart(2, '0');
  const seconds = String(date.getSeconds()).padStart(2, '0');
  
  return `${year}-${month}-${day}T${hours}:${minutes}:${seconds}.000${offsetSign}${String(offsetHours).padStart(2, '0')}:${String(offsetMins).padStart(2, '0')}`;
};

// Modal component for editing offers
const EditOfferModal = ({ offer, onClose, onUpdate }) => {
  const [formData, setFormData] = useState({
    type: offer.type || '',
    adType: offer.adType || '',
    title: offer.title || '',
    messageDescription: offer.messageDescription || '',
    expirationDate: offer.expirationDate ? new Date(offer.expirationDate).toISOString().slice(0, 16) : '',
    time: offer.time || [],
    distanceLimit: offer.distanceLimit || '',
    restaurant: offer.restaurant?._id || offer.restaurant || '',
    restaurantName: offer.restaurantName || offer.restaurant?.name || '',
    offerDeleteDate: offer.offerDeleteDate ? new Date(offer.offerDeleteDate).toISOString().slice(0, 16) : ''
  });
  const [selectedImage, setSelectedImage] = useState(null);
  const [imagePreview, setImagePreview] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [restaurants, setRestaurants] = useState([]);
  const [selectedRestaurantId, setSelectedRestaurantId] = useState(offer.restaurant?._id || offer.restaurant || '');
  const [typedRestaurantName, setTypedRestaurantName] = useState('');
  const [selectedTimeBlocks, setSelectedTimeBlocks] = useState([]);

  const typeOptions = ['رویداد', 'تازه', 'پیشنهاد', 'تفریحات', 'آموزش', 'سرگرمی', 'دورهمی'];
  const adTypeOptions = [
    { value: 'dynamic', label: 'Dynamic' },
    { value: 'static', label: 'Static' }
  ];
  
  const timeBlocks = [
    { id: 1, label: 'صبح', minTime: '07:00', maxTime: '11:59', startTime: '', endTime: '' },
    { id: 2, label: 'ظهر', minTime: '12:00', maxTime: '15:59', startTime: '', endTime: '' },
    { id: 3, label: 'عصر', minTime: '16:00', maxTime: '19:29', startTime: '', endTime: '' },
    { id: 4, label: 'شب', minTime: '19:30', maxTime: '23:58', startTime: '', endTime: '' }
  ];

  useEffect(() => {
    fetchRestaurants();
    initializeTimeBlocks();
  }, []);

  const fetchRestaurants = async () => {
    try {
  const response = await fetch('https://apipublic.menuoff.com/restaurants-public');
      if (response.ok) {
        const data = await response.json();
        if (data.ok) {
          setRestaurants(data.restaurants);
        }
      }
    } catch (error) {
      console.error('Error fetching restaurants:', error);
    }
  };

  const initializeTimeBlocks = () => {
    if (offer.time && offer.time.length > 0) {
      const initializedBlocks = timeBlocks.map(block => {
        const existingTime = offer.time.find(t => t.label === block.label);
        if (existingTime) {
          return {
            ...block,
            startTime: existingTime.startTime || '',
            endTime: existingTime.endTime || ''
          };
        }
        return block;
      });
      setSelectedTimeBlocks(initializedBlocks.filter(block => 
        offer.time.some(t => t.label === block.label)
      ));
    }
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleRestaurantDropdownChange = (e) => {
    const selectedId = e.target.value;
    setSelectedRestaurantId(selectedId);
    
    if (selectedId) {
      const selectedRestaurant = restaurants.find(r => r._id === selectedId);
      const restaurantName = selectedRestaurant ? selectedRestaurant.name : '';
      
      setTypedRestaurantName('');
      setFormData(prev => ({
        ...prev,
        restaurant: selectedId,
        restaurantName: restaurantName
      }));
    } else {
      setFormData(prev => ({
        ...prev,
        restaurant: '',
        restaurantName: ''
      }));
    }
  };

  const handleTypedRestaurantChange = (e) => {
    const typedName = e.target.value;
    setTypedRestaurantName(typedName);
    
    if (typedName) {
      setSelectedRestaurantId('');
      setFormData(prev => ({
        ...prev,
        restaurant: typedName,
        restaurantName: typedName
      }));
    } else {
      setFormData(prev => ({
        ...prev,
        restaurant: '',
        restaurantName: ''
      }));
    }
  };

  const handleTimeBlockToggle = (blockId) => {
    const block = timeBlocks.find(b => b.id === blockId);
    const isSelected = selectedTimeBlocks.some(b => b.id === blockId);
    
    if (isSelected) {
      setSelectedTimeBlocks(selectedTimeBlocks.filter(b => b.id !== blockId));
    } else {
      setSelectedTimeBlocks([...selectedTimeBlocks, { ...block }]);
    }
  };

  const handleTimeChange = (blockId, field, value) => {
    const block = timeBlocks.find(b => b.id === blockId);
    
    // Validate time is within the block's allowed range
    if (value < block.minTime || value > block.maxTime) {
      setError(`زمان ${field === 'startTime' ? 'شروع' : 'پایان'} باید بین ${block.minTime} تا ${block.maxTime} باشد`);
      return;
    }
    
    setSelectedTimeBlocks(selectedTimeBlocks.map(selectedBlock => 
      selectedBlock.id === blockId ? { ...selectedBlock, [field]: value } : selectedBlock
    ));
    
    // Clear error if time is valid
    setError('');
  };

  const handleImageChange = (e) => {
    const file = e.target.files[0];
    
    if (!file) {
      setSelectedImage(null);
      setImagePreview(null);
      return;
    }

    // Check file size (200KB = 200 * 1024 bytes)
    const maxSize = 200 * 1024;
    if (file.size > maxSize) {
      setError('حجم تصویر نباید بیشتر از 200 کیلوبایت باشد');
      e.target.value = '';
      return;
    }

    // Check file type
    const allowedTypes = ['image/jpeg', 'image/jpg', 'image/png', 'image/gif'];
    if (!allowedTypes.includes(file.type)) {
      setError('فقط فایل‌های تصویری (JPG, PNG, GIF) مجاز هستند');
      e.target.value = '';
      return;
    }

    setSelectedImage(file);
    setError('');

    // Create preview
    const reader = new FileReader();
    reader.onload = (e) => {
      setImagePreview(e.target.result);
    };
    reader.readAsDataURL(file);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError('');

    // Validate time blocks with range checking
    const validTimeBlocks = selectedTimeBlocks.filter(block => {
      if (!block.startTime || !block.endTime) return false;
      
      // Find the original time block definition
      const originalBlock = timeBlocks.find(tb => tb.id === block.id);
      if (!originalBlock) return false;
      
      // Check if times are within allowed range
      const startValid = block.startTime >= originalBlock.minTime && block.startTime <= originalBlock.maxTime;
      const endValid = block.endTime >= originalBlock.minTime && block.endTime <= originalBlock.maxTime;
      const orderValid = block.startTime < block.endTime;
      
      return startValid && endValid && orderValid;
    }).map(block => ({
      label: block.label,
      startTime: block.startTime,
      endTime: block.endTime
    }));

    try {
      const finalRestaurantValue = selectedRestaurantId || typedRestaurantName.trim();
      
      const formDataToSend = new FormData();
      
      // Add all form fields
      formDataToSend.append('type', formData.type);
      formDataToSend.append('adType', formData.adType);
      formDataToSend.append('title', formData.title);
      formDataToSend.append('messageDescription', formData.messageDescription);
      if (formData.expirationDate) {
        formDataToSend.append('expirationDate', formatDateTimeForBackend(formData.expirationDate));
      }
      formDataToSend.append('time', JSON.stringify(validTimeBlocks));
      formDataToSend.append('distanceLimit', formData.distanceLimit);
      formDataToSend.append('restaurant', finalRestaurantValue);
      formDataToSend.append('restaurantName', formData.restaurantName);
      if (formData.offerDeleteDate) {
        formDataToSend.append('offerDeleteDate', formatDateTimeForBackend(formData.offerDeleteDate));
      }
      
      // Add image if selected
      if (selectedImage) {
        formDataToSend.append('picture', selectedImage);
      }

      const response = await authenticatedRequest('PUT', `/offers/${offer._id}`, formDataToSend);

      if (response.data.ok) {
        onUpdate(); // Refresh the offers list
        onClose(); // Close the modal
      } else {
        setError(response.data.message || 'خطا در به‌روزرسانی آفر');
      }
    } catch (err) {
      console.error('Error updating offer:', err);
      setError('خطا در اتصال به سرور');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="modal-overlay">
      <div className="modal-content">
        <div className="modal-header">
          <h3>ویرایش آفر</h3>
          <button onClick={onClose} className="close-btn">×</button>
        </div>

        {error && <div className="error-message">{error}</div>}

        <form onSubmit={handleSubmit} className="edit-offer-form">
          <div className="form-row">
            <div className="form-group">
              <label htmlFor="type">نوع آفر:</label>
              <select
                id="type"
                name="type"
                value={formData.type}
                onChange={handleChange}
                required
              >
                <option value="">انتخاب نوع آفر</option>
                {typeOptions.map((option, index) => (
                  <option key={index} value={option}>
                    {option}
                  </option>
                ))}
              </select>
            </div>

            <div className="form-group">
              <label htmlFor="adType">نوع تبلیغ:</label>
              <select
                id="adType"
                name="adType"
                value={formData.adType}
                onChange={handleChange}
                required
              >
                <option value="">انتخاب نوع تبلیغ</option>
                {adTypeOptions.map((option, index) => (
                  <option key={index} value={option.value}>
                    {option.label}
                  </option>
                ))}
              </select>
            </div>
          </div>

          <div className="form-row">
            <div className="form-group">
              <label htmlFor="title">عنوان آفر:</label>
              <input
                type="text"
                id="title"
                name="title"
                value={formData.title}
                onChange={handleChange}
                required
                placeholder="عنوان آفر"
              />
            </div>
          </div>

          <div className="form-group">
            <label htmlFor="messageDescription">توضیحات:</label>
            <textarea
              id="messageDescription"
              name="messageDescription"
              value={formData.messageDescription}
              onChange={handleChange}
              required
              placeholder="توضیحات آفر (حداکثر 100 کاراکتر)"
              rows="3"
              maxLength="100"
            />
            <div className="character-count">
              {formData.messageDescription.length}/100 کاراکتر
            </div>
          </div>

          <div className="form-row">
            <div className="form-group">
              <label htmlFor="expirationDate">تاریخ انقضا:</label>
              <input
                type="datetime-local"
                id="expirationDate"
                name="expirationDate"
                value={formData.expirationDate}
                onChange={handleChange}
                required
              />
            </div>

            <div className="form-group">
              <label htmlFor="distanceLimit">محدوده فاصله (متر):</label>
              <input
                type="number"
                id="distanceLimit"
                name="distanceLimit"
                value={formData.distanceLimit}
                onChange={handleChange}
                required
                placeholder="1000"
              />
            </div>
          </div>

          <div className="restaurant-section">
            <label>رستوران:</label>
            <div className="restaurant-fields">
              <div className="form-group">
                <label htmlFor="restaurant-dropdown">انتخاب از لیست:</label>
                <select
                  id="restaurant-dropdown"
                  value={selectedRestaurantId}
                  onChange={handleRestaurantDropdownChange}
                  className="restaurant-dropdown"
                >
                  <option value="">انتخاب رستوران از لیست</option>
                  {restaurants.map(restaurant => (
                    <option key={restaurant._id} value={restaurant._id}>
                      {restaurant.name}
                    </option>
                  ))}
                </select>
              </div>

              <div className="form-group">
                <label htmlFor="restaurant-typed">یا نام رستوران را تایپ کنید:</label>
                <input
                  type="text"
                  id="restaurant-typed"
                  value={typedRestaurantName}
                  onChange={handleTypedRestaurantChange}
                  placeholder="نام رستوران جدید را تایپ کنید"
                  className="restaurant-typed"
                />
              </div>
            </div>
          </div>

          <div className="time-blocks-section">
            <label>بازه‌های زمانی:</label>
            <div className="time-blocks-grid">
              {timeBlocks.map(block => {
                const isSelected = selectedTimeBlocks.some(b => b.id === block.id);
                const selectedBlock = selectedTimeBlocks.find(b => b.id === block.id);
                
                return (
                  <div key={block.id} className={`time-block ${isSelected ? 'selected' : ''}`}>
                    <div className="time-block-header">
                      <label className="time-block-checkbox">
                        <input
                          type="checkbox"
                          checked={isSelected}
                          onChange={() => handleTimeBlockToggle(block.id)}
                        />
                        <span>{block.label}</span>
                      </label>
                    </div>
                    
                    {isSelected && (
                      <div className="time-inputs">
                        <div className="time-input-group">
                          <label>شروع:</label>
                          <input
                            type="time"
                            min={block.minTime}
                            max={block.maxTime}
                            value={selectedBlock?.startTime || ''}
                            onChange={(e) => handleTimeChange(block.id, 'startTime', e.target.value)}
                          />
                        </div>
                        <div className="time-input-group">
                          <label>پایان:</label>
                          <input
                            type="time"
                            min={block.minTime}
                            max={block.maxTime}
                            value={selectedBlock?.endTime || ''}
                            onChange={(e) => handleTimeChange(block.id, 'endTime', e.target.value)}
                          />
                        </div>
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
            <p className="time-blocks-note">
              * حداکثر 4 بازه زمانی قابل انتخاب است
            </p>
          </div>

          <div className="form-group">
            <label htmlFor="picture">تصویر آفر:</label>
            <input
              type="file"
              id="picture"
              accept="image/*"
              onChange={handleImageChange}
            />
            
            {(imagePreview || offer.picture) && (
              <div className="image-preview">
                <img 
                  src={imagePreview || (offer.picture.startsWith('http') ? offer.picture : `https://apipublic.menuoff.com/${offer.picture}`)} 
                  alt="پیش‌نمایش" 
                />
              </div>
            )}
          </div>

          <div className="form-actions">
            <button type="button" onClick={onClose} className="cancel-button">
              انصراف
            </button>
            <button type="submit" disabled={loading} className="submit-button">
              {loading ? 'در حال به‌روزرسانی...' : 'به‌روزرسانی آفر'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default OffersList;
